import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  IconButton,
  Snackbar,
  Slide
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const SlideTransition = (props) => {
  return <Slide {...props} direction="down" />;
};

const VerificationSuccessNotification = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Check for verification success message in sessionStorage
    const checkVerificationSuccess = () => {
      console.log('🔍 [NOTIFICATION] Checking for verification success message...');
      try {
        const storedSuccess = sessionStorage.getItem('emailVerificationSuccess');
        console.log('🔍 [NOTIFICATION] Stored success data:', storedSuccess);
        
        if (storedSuccess) {
          const successData = JSON.parse(storedSuccess);
          console.log('🔍 [NOTIFICATION] Parsed success data:', successData);
          
          // Check if the message is recent (within last 5 minutes)
          const messageAge = Date.now() - successData.timestamp;
          const fiveMinutes = 5 * 60 * 1000;
          console.log('🔍 [NOTIFICATION] Message age:', messageAge, 'ms (limit:', fiveMinutes, 'ms)');
          
          if (messageAge < fiveMinutes) {
            console.log('🟢 [NOTIFICATION] Message is recent, showing success notification');
            setSuccessMessage(successData.message);
            setShowSuccess(true);
            
            // Clear the message from sessionStorage after showing it
            console.log('🟢 [NOTIFICATION] Clearing message from sessionStorage');
            sessionStorage.removeItem('emailVerificationSuccess');
          } else {
            // Message is too old, remove it
            console.log('🟡 [NOTIFICATION] Message is too old, removing from sessionStorage');
            sessionStorage.removeItem('emailVerificationSuccess');
          }
        } else {
          console.log('🔍 [NOTIFICATION] No verification success message found in sessionStorage');
        }
      } catch (error) {
        console.error('🔴 [NOTIFICATION] Error checking verification success:', error);
        // Clear invalid data
        sessionStorage.removeItem('emailVerificationSuccess');
      }
    };

    // Check immediately when component mounts
    checkVerificationSuccess();
    
    // Also check when the page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔍 [NOTIFICATION] Page is visible, checking for verification success message...');
        checkVerificationSuccess();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleClose = (event, reason) => {
    // Only allow manual dismissal via close button, not clickaway
    if (reason === 'clickaway') {
      return;
    }
    setShowSuccess(false);
  };

  return (
    <Snackbar
      open={showSuccess}
      // Removed autoHideDuration - user must manually dismiss
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
      sx={{ mt: 8 }} // Add margin to avoid overlapping with app bar
    >
      <Alert
        onClose={handleClose}
        severity="success"
        variant="filled"
        sx={{
          width: '100%',
          maxWidth: 500,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        icon={<CheckIcon />}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
          Email Verified Successfully!
        </AlertTitle>
        <Box component="span" sx={{ fontSize: '0.875rem' }}>
          {successMessage}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default VerificationSuccessNotification;
